/**
 * SaaS setup guides.
 *
 * The guides used to live in Contentful and were fetched at runtime. They are
 * now Markdown files in this repository under `public/setup-guides/`, served
 * as static assets alongside the bundle. Edit the Markdown directly;
 * `tools/fetch-setup-guides.mjs` exists only to re-import from Contentful.
 */

const guidePath = (saasIdentifier: string) =>
  `/setup-guides/${saasIdentifier}.md`

/** Thrown when a SaaS has no guide of its own, so the generic one is shown. */
export class SetupGuideNotFoundError extends Error {
  constructor(saasIdentifier: string) {
    super(`No setup guide found for ${saasIdentifier}`)
    this.name = 'SetupGuideNotFoundError'
  }
}

export const getSetupGuide = async (
  saasIdentifier: string | undefined,
  signal?: AbortSignal
): Promise<string> => {
  if (!saasIdentifier) {
    throw new SetupGuideNotFoundError('(no SaaS identifier)')
  }

  const response = await fetch(guidePath(saasIdentifier), { signal })

  if (!response.ok) {
    throw new SetupGuideNotFoundError(saasIdentifier)
  }

  const markdown = await response.text()

  // A missing file can be served as the SPA fallback (index.html) rather than
  // a 404, so reject anything that is plainly not Markdown.
  if (markdown.trimStart().toLowerCase().startsWith('<!doctype html')) {
    throw new SetupGuideNotFoundError(saasIdentifier)
  }

  return markdown
}
